import { useState } from 'react'
import type { TimeSlotSelection } from '../types'
import { useScheduleAppointmentBooking } from './useScheduleAppointmentBooking'
import { useScheduleAppointmentLocations } from './useScheduleAppointmentLocations'
import { useVisibleWebinarEvents } from './useVisibleWebinarEvents'

export function useScheduleAppointmentPage() {
  const [selectedLocationExternalId, setSelectedLocationExternalId] = useState<string | null>(null)
  const [selectedIsoDate, setSelectedIsoDate] = useState('')
  const [selection, setSelection] = useState<TimeSlotSelection | null>(null)
  const { branchLocations, branchesLoading, locationOptions } = useScheduleAppointmentLocations()
  const { visibleWebinarEvents } = useVisibleWebinarEvents(selectedIsoDate)
  const isSelectedOnlineSessionVisible =
    selection?.type !== 'online'
      ? true
      : visibleWebinarEvents.some(event => event.id === selection.eventId)

  const canContinue = Boolean(
    selectedLocationExternalId && selectedIsoDate && selection && isSelectedOnlineSessionVisible
  )
  const {
    bookAppointment,
    bookingError,
    handleContinue,
    handleOnlineRegistrationContinue,
    navigate,
    registeredOnlineSession,
    setBookingError
  } = useScheduleAppointmentBooking({
    canContinue,
    selectedIsoDate,
    selectedLocationExternalId,
    selection
  })

  const handleLocationSelect = (value: string) => {
    const locationMatch = branchLocations.find(
      location => location.externalId === value || String(location.id) === value
    )

    setSelectedLocationExternalId(locationMatch?.externalId ?? value ?? null)
    setSelectedIsoDate('')
    setSelection(null)
  }

  const selectTimeSlot = (
    time: string,
    rawTime: string,
    type: 'inPerson' | 'phone' | 'online',
    eventId?: number,
    eventUrl?: string,
    scheduledStartAt?: string
  ) => {
    setSelection({ time, rawTime, type, eventId, eventUrl, scheduledStartAt })
  }

  const handleDatePickerChange = (value: string) => {
    if (!value) {
      setSelectedIsoDate('')
      setSelection(null)
      return
    }

    setSelectedIsoDate(value)
    setSelection(null)
  }

  return {
    bookAppointment,
    bookingError,
    branchesLoading,
    canContinue,
    handleContinue,
    handleDatePickerChange,
    handleLocationSelect,
    handleOnlineRegistrationContinue,
    isSelectedOnlineSessionVisible,
    locationOptions,
    navigate,
    registeredOnlineSession,
    selectTimeSlot,
    selectedIsoDate,
    selectedLocationExternalId,
    selection,
    setBookingError,
    visibleWebinarEvents
  }
}
